import React, { useState, useEffect } from 'react';
import { collection, doc, getDoc, setDoc, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { Mail, Settings, Link2, Plus, Save, RefreshCw, CheckCircle, AlertCircle, Eye, EyeOff, Building2, X, Calendar, Info } from 'lucide-react';

function LabEmailSettings({ user }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [settings, setSettings] = useState({
    email: '',
    emailPassword: '',
    emailEnabled: false,
    emailProvider: 'naver',
    lastSyncAt: null  });
  
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''  });
  const [selectedClinicForSync, setSelectedClinicForSync] = useState('');
  
  const [clinicMappings, setClinicMappings] = useState([]);
  const [connectedClinics, setConnectedClinics] = useState([]);
  const [newMapping, setNewMapping] = useState({ clinicId: '', clinicName: '', email: '' });
  const [isManualInput, setIsManualInput] = useState(false);

  const getLabId = () => user?.companyId || user?.uid || currentUser?.uid;

  useEffect(() => {
    if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 3000); return () => clearTimeout(t); }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) { const t = setTimeout(() => setErrorMessage(''), 3000); return () => clearTimeout(t); }
  }, [errorMessage]);

  useEffect(() => {
    const fetchSettings = async () => {
      const labId = getLabId();
      if (!labId) return;
      
      try {
        const settingsDoc = await getDoc(doc(db, 'labSettings', labId));
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setSettings({
            email: data.email || '',
            emailPassword: data.emailPassword || '',
            emailEnabled: data.emailEnabled || false,
            emailProvider: data.emailProvider || 'naver',
            lastSyncAt: data.lastEmailSyncAt?.toDate() || null
          });
          
          if (data.clinicEmailMapping) {
            const mappings = Object.entries(data.clinicEmailMapping).map(([key, mapData]) => ({
              clinicId: mapData.clinicId || null,
              clinicName: mapData.clinicName || key,
              emails: mapData.emails ? mapData.emails : (Array.isArray(mapData) ? mapData : [mapData])
            }));
            setClinicMappings(mappings);
          }
        }
        
        await fetchConnectedClinics(labId);
      } catch (error) {
        console.error('설정 불러오기 오류:', error);
        setErrorMessage('설정을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [user, currentUser]);

  const fetchConnectedClinics = async (labId) => {
    try {
      console.log('fetchConnectedClinics 호출, labId:', labId);
      const partnersMap = new Map();

      // 2026-09-10 보안 수정: connections 읽기 rules 가 당사자 한정으로 바뀌어 status 만 거는 전체 스캔은 거부된다.
      //   labId 가 requesterId / receiverId / recipientId 인 accepted 연결만 3갈래로 조회해 합친다.
      //   (아래 루프의 방향 판정은 그대로 — 결과 집합만 좁아진다)
      const connectionsRef = collection(db, 'connections');
      const [sentSnap, recvSnap, recipSnap] = await Promise.all([
        getDocs(query(connectionsRef, where('requesterId', '==', labId), where('status', '==', 'accepted'))),
        getDocs(query(connectionsRef, where('receiverId', '==', labId), where('status', '==', 'accepted'))),
        getDocs(query(connectionsRef, where('recipientId', '==', labId), where('status', '==', 'accepted'))),
      ]);
      const seenConn = new Set();
      const connectionsSnapshot = {
        docs: [...sentSnap.docs, ...recvSnap.docs, ...recipSnap.docs].filter(d => {
          if (seenConn.has(d.id)) return false;
          seenConn.add(d.id);
          return true;
        }),
      };
      console.log('connections 개수:', connectionsSnapshot.docs.length);

      for (const docSnap of connectionsSnapshot.docs) {
        const connection = docSnap.data();

        // 내가 요청을 보낸 경우 (상대방이 receiverId)
        if (connection.requesterId === labId && connection.receiverId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', connection.receiverId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              partnersMap.set(connection.receiverId, {
                id: connection.receiverId,
                name: userData.businessName || userData.companyName || userData.name || '이름 없음',
                email: userData.email
              });
            }
          } catch (error) {
            console.error('사용자 정보 조회 실패:', error);
          }
        }

        // 내가 요청을 받은 경우 (상대방이 requesterId)
        if (connection.receiverId === labId && connection.requesterId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', connection.requesterId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              partnersMap.set(connection.requesterId, {
                id: connection.requesterId,
                name: userData.businessName || userData.companyName || userData.name || '이름 없음',
                email: userData.email
              });
            }
          } catch (error) {
            console.error('사용자 정보 조회 실패:', error);
          }
        }
      }

      const clinics = Array.from(partnersMap.values());
      console.log('연결된 치과 목록:', clinics);
      setConnectedClinics(clinics);
    } catch (error) {
      console.error('치과 목록 오류:', error);
    }
  };

  const getClinicName = (mapping) => {
    if (mapping.clinicId) {
      const clinic = connectedClinics.find(c => c.id === mapping.clinicId);
      return clinic?.name || mapping.clinicName || mapping.clinicId;
    }
    return mapping.clinicName;
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const labId = getLabId();
      
      // 새로운 clinicEmailMapping 생성
      const clinicEmailMapping = {};
      clinicMappings.forEach(m => {
        const key = m.clinicId || m.clinicName;
        clinicEmailMapping[key] = { 
          emails: m.emails, 
          clinicName: m.clinicName,
          clinicId: m.clinicId || null
        };
      });
      
      // 기존 데이터 가져오기 (다른 필드 보존)
      const existingDoc = await getDoc(doc(db, 'labSettings', labId));
      const existingData = existingDoc.exists() ? existingDoc.data() : {};
      
      // 완전히 새로운 문서로 저장 (clinicEmailMapping 덮어쓰기)
      await setDoc(doc(db, 'labSettings', labId), {
        ...existingData,
        email: settings.email,
        emailPassword: settings.emailPassword,
        emailEnabled: settings.emailEnabled,
        emailProvider: settings.emailProvider,
        clinicEmailMapping,  // 완전히 새 값으로 교체
        updatedAt: new Date()
      });
      
      setSuccessMessage('설정이 저장되었습니다.');
    } catch (error) {
      console.error('저장 오류:', error);
      setErrorMessage('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    if ((dateRange.startDate || dateRange.endDate) && !selectedClinicForSync) {
      setErrorMessage('과거 의뢰서를 가져오려면 치과를 선택해주세요.');
      return;
    }
    if (clinicMappings.length === 0) {
      setErrorMessage('치과 이메일 매핑을 먼저 추가해주세요.');
      return;
    }
    
    setSyncing(true);
    
    try {
      const functions = getFunctions(undefined, 'asia-northeast3');
      const syncEmailsManual = httpsCallable(functions, 'syncEmailsManual');
      
      const params = { labId: getLabId() };
      if (dateRange.startDate) {
        params.startDate = new Date(dateRange.startDate).toISOString();
      }
      if (dateRange.endDate) {
        const endDate = new Date(dateRange.endDate);
        endDate.setDate(endDate.getDate() + 1);
        params.endDate = endDate.toISOString();
      }
      if (selectedClinicForSync) {
        params.filterClinicId = selectedClinicForSync;
      }
      
      const result = await syncEmailsManual(params);
      
      setSuccessMessage(`동기화 완료! ${result.data?.count || 0}개의 의뢰서를 가져왔습니다.`);
      setSettings(prev => ({ ...prev, lastSyncAt: new Date() }));
    } catch (error) {
      console.error('동기화 오류:', error);
      setErrorMessage(error.message || '동기화에 실패했습니다.');
    } finally {
      setSyncing(false);
    }
  };

  const addClinicMapping = () => {
    // 직접 입력 모드
    if (isManualInput) {
      if (!newMapping.clinicName || !newMapping.email) {
        setErrorMessage('치과 이름과 이메일을 입력해주세요.');
        return;
      }
    } else {
      // 선택 모드
      if (!newMapping.clinicId || !newMapping.email) {
        setErrorMessage('치과와 이메일을 선택/입력해주세요.');
        return;
      }
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMapping.email)) {
      setErrorMessage('올바른 이메일 형식을 입력해주세요.');
      return;
    }
    
    const clinicName = isManualInput 
      ? newMapping.clinicName 
      : connectedClinics.find(c => c.id === newMapping.clinicId)?.name || newMapping.clinicId;
    
    const key = isManualInput ? clinicName : newMapping.clinicId;
    const existing = clinicMappings.find(m => 
      (m.clinicId && m.clinicId === key) || (m.clinicName === key)
    );
    
    if (existing) {
      if (existing.emails.includes(newMapping.email)) {
        setErrorMessage('이미 등록된 이메일입니다.');
        return;
      }
      setClinicMappings(prev => prev.map(m => 
        ((m.clinicId && m.clinicId === key) || m.clinicName === key)
          ? { ...m, emails: [...m.emails, newMapping.email] }
          : m
      ));
    } else {
      setClinicMappings(prev => [...prev, {
        clinicId: isManualInput ? null : newMapping.clinicId,
        clinicName: clinicName,
        emails: [newMapping.email]
      }]);
    }
    setNewMapping({ clinicId: '', clinicName: '', email: '' });
    setSuccessMessage('매핑이 추가되었습니다. 설정 저장을 눌러주세요.');
  };

  const removeClinicMapping = (mapping, emailToRemove) => {
    setClinicMappings(prev => {
      return prev.map(m => {
        const isSameMapping = (m.clinicId && mapping.clinicId && m.clinicId === mapping.clinicId) ||
                              (m.clinicName === mapping.clinicName && !m.clinicId && !mapping.clinicId);
        if (isSameMapping) {
          const newEmails = m.emails.filter(e => e !== emailToRemove);
          return newEmails.length > 0 ? { ...m, emails: newEmails } : null;
        }
        return m;
      }).filter(Boolean);
    });
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>불러오는 중...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {successMessage && (
        <div style={styles.successMsg}>
          <CheckCircle size={20} color="#16a34a" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div style={styles.errorMsg}>
          <AlertCircle size={20} color="#991b1b" />
          {errorMessage}
        </div>
      )}

      {/* 이메일 제공자 선택 */}
      <div style={styles.providerCard}>
        <div style={styles.providerHeader}>
          <Mail size={22} color="#6366f1" />
          <h3 style={styles.providerTitle}>이메일 제공자 선택</h3>
        </div>
        <div style={styles.providerTabs}>
          <button
            onClick={() => setSettings(prev => ({ ...prev, emailProvider: 'naver' }))}
            style={{
              ...styles.providerTab,
              ...(settings.emailProvider === 'naver' ? styles.providerTabActive : {})
            }}
          >
            <span style={styles.providerIcon}>N</span>
            네이버
          </button>
          <button
            onClick={() => setSettings(prev => ({ ...prev, emailProvider: 'gmail' }))}
            style={{
              ...styles.providerTab,
              ...(settings.emailProvider === 'gmail' ? styles.providerTabActiveGmail : {})
            }}
          >
            <span style={{...styles.providerIcon, backgroundColor: '#ea4335', color: '#fff'}}>G</span>
            Gmail
          </button>
          <button
            onClick={() => setSettings(prev => ({ ...prev, emailProvider: 'daum' }))}
            style={{
              ...styles.providerTab,
              ...(settings.emailProvider === 'daum' ? styles.providerTabActiveDaum : {})
            }}
          >
            <span style={{...styles.providerIcon, backgroundColor: '#ffcd00', color: '#000'}}>D</span>
            다음/카카오
          </button>
        </div>
      </div>

      {/* 필수 설정 안내 - 네이버 */}
      {settings.emailProvider === 'naver' && (
        <div style={styles.guideCard}>
          <div style={styles.guideHeader}>
            <Info size={22} color="#0369a1" />
            <h3 style={styles.guideTitle}>📋 네이버 메일 연동 전 필수 설정</h3>
          </div>
          <div style={styles.guideBody}>
            <div style={styles.guideItem}>
              <div style={styles.guideStep}>1</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>IMAP/SMTP 사용 설정</p>
                <p style={styles.guideText}>
                  네이버 메일 → <strong>환경설정</strong> → <strong>POP3/IMAP 설정</strong> → IMAP/SMTP 사용을 <strong>'사용함'</strong>으로 변경
                </p>
              </div>
            </div>
            <div style={styles.guideItem}>
              <div style={{...styles.guideStep, backgroundColor: '#dc2626'}}>2</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>⚠️ 해외 로그인 차단 해제 (필수!)</p>
                <p style={styles.guideText}>
                  네이버 → <strong>내정보</strong> → <strong>보안설정</strong> → <strong>해외 로그인 차단</strong> → <strong>'차단 해제'</strong>로 변경
                </p>
                <p style={styles.guideWarning}>* Firebase 서버가 해외에 있어 차단 해제가 필수입니다</p>
              </div>
            </div>
            <div style={styles.guideItem}>
              <div style={styles.guideStep}>3</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>앱 비밀번호 발급 (2단계 인증 사용 시)</p>
                <p style={styles.guideText}>
                  네이버 → <strong>내정보</strong> → <strong>보안설정</strong> → <strong>2단계 인증</strong> → <strong>앱 비밀번호 관리</strong> → 비밀번호 생성
                </p>
                <p style={styles.guideSubtext}>* 2단계 인증 미사용 시 일반 비밀번호 입력</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 필수 설정 안내 - Gmail */}
      {settings.emailProvider === 'gmail' && (
        <div style={{...styles.guideCard, borderColor: '#ea4335'}}>
          <div style={{...styles.guideHeader, backgroundColor: '#fef2f2', borderColor: '#fecaca'}}>
            <Info size={22} color="#dc2626" />
            <h3 style={{...styles.guideTitle, color: '#dc2626'}}>📋 Gmail 연동 전 필수 설정</h3>
          </div>
          <div style={styles.guideBody}>
            <div style={styles.guideItem}>
              <div style={{...styles.guideStep, backgroundColor: '#ea4335'}}>1</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>2단계 인증 활성화</p>
                <p style={styles.guideText}>
                  Google 계정 → <strong>보안</strong> → <strong>2단계 인증</strong> → 활성화
                </p>
                <p style={styles.guideSubtext}>* 2단계 인증이 활성화되어야 앱 비밀번호 생성 가능</p>
              </div>
            </div>
            <div style={styles.guideItem}>
              <div style={{...styles.guideStep, backgroundColor: '#ea4335'}}>2</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>앱 비밀번호 생성</p>
                <p style={styles.guideText}>
                  Google 계정 → <strong>보안</strong> → <strong>2단계 인증</strong> → <strong>앱 비밀번호</strong> → 새 비밀번호 생성
                </p>
                <p style={styles.guideSubtext}>* 앱 이름: "DentCluster" 입력 후 생성된 16자리 비밀번호 사용</p>
              </div>
            </div>
            <div style={styles.guideItem}>
              <div style={{...styles.guideStep, backgroundColor: '#ea4335'}}>3</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>IMAP 사용 설정</p>
                <p style={styles.guideText}>
                  Gmail → <strong>설정(톱니바퀴)</strong> → <strong>모든 설정 보기</strong> → <strong>전달 및 POP/IMAP</strong> → IMAP 사용으로 변경
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 필수 설정 안내 - 다음/카카오 */}
      {settings.emailProvider === 'daum' && (
        <div style={{...styles.guideCard, borderColor: '#ffcd00'}}>
          <div style={{...styles.guideHeader, backgroundColor: '#fefce8', borderColor: '#fef08a'}}>
            <Info size={22} color="#ca8a04" />
            <h3 style={{...styles.guideTitle, color: '#a16207'}}>📋 다음/카카오 메일 연동 전 필수 설정</h3>
          </div>
          <div style={styles.guideBody}>
            <div style={styles.guideItem}>
              <div style={{...styles.guideStep, backgroundColor: '#eab308'}}>1</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>외부메일 사용을 위한 비밀번호 설정</p>
                <p style={styles.guideText}>
                  Daum 메일 → <strong>환경설정</strong> → <strong>외부메일</strong> → <strong>외부메일 사용을 위한 비밀번호 설정</strong>
                </p>
                <p style={styles.guideSubtext}>* 기존 카카오 로그인 비밀번호와 다른 별도 비밀번호</p>
              </div>
            </div>
            <div style={styles.guideItem}>
              <div style={{...styles.guideStep, backgroundColor: '#eab308'}}>2</div>
              <div style={styles.guideContent}>
                <p style={styles.guideLabel}>IMAP/SMTP 사용 설정</p>
                <p style={styles.guideText}>
                  Daum 메일 → <strong>환경설정</strong> → <strong>외부메일</strong> → <strong>IMAP/SMTP 사용</strong> → '사용함'으로 변경
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <div style={{ ...styles.sectionIconBox, backgroundColor: '#ede9fe' }}>
            <Mail size={22} color="#6366f1" />
          </div>
          <h2 style={styles.sectionTitle}>이메일 의뢰서 연동 설정</h2>
        </div>
        
        <div style={styles.cardBody}>
          <p style={styles.description}>
            {settings.emailProvider === 'naver' && '네이버 이메일로 받은 덴트웹, R2CAD 기공의뢰서를 자동으로 가져옵니다.'}
            {settings.emailProvider === 'gmail' && 'Gmail로 받은 덴트웹, R2CAD 기공의뢰서를 자동으로 가져옵니다.'}
            {settings.emailProvider === 'daum' && '다음/카카오 메일로 받은 덴트웹, R2CAD 기공의뢰서를 자동으로 가져옵니다.'}
          </p>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>이메일 주소</label>
            <div style={styles.inputBox}>
              <Mail size={20} color="#94a3b8" />
              <input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                placeholder={
                  settings.emailProvider === 'naver' ? 'example@naver.com' :
                  settings.emailProvider === 'gmail' ? 'example@gmail.com' :
                  'example@daum.net'
                }
                style={styles.input}
              />
            </div>
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>
              {settings.emailProvider === 'naver' && '앱 비밀번호 (2단계 인증 사용 시) 또는 일반 비밀번호'}
              {settings.emailProvider === 'gmail' && '앱 비밀번호 (16자리)'}
              {settings.emailProvider === 'daum' && '외부메일 비밀번호'}
            </label>
            <div style={styles.inputBox}>
              <Settings size={20} color="#94a3b8" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={settings.emailPassword}
                onChange={(e) => setSettings(prev => ({ ...prev, emailPassword: e.target.value }))}
                placeholder={
                  settings.emailProvider === 'naver' ? '앱 비밀번호 또는 일반 비밀번호 입력' :
                  settings.emailProvider === 'gmail' ? '16자리 앱 비밀번호 입력' :
                  '외부메일 비밀번호 입력'
                }
                style={styles.input}
              />
              <button style={styles.eyeBtn} onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={20} color="#94a3b8" /> : <Eye size={20} color="#94a3b8" />}
              </button>
            </div>
          </div>
          
          <div style={styles.toggleRow}>
            <label style={styles.toggleContainer}>
              <input
                type="checkbox"
                checked={settings.emailEnabled}
                onChange={(e) => setSettings(prev => ({ ...prev, emailEnabled: e.target.checked }))}
                style={styles.toggleInput}
              />
              <span style={{ ...styles.toggleSwitch, backgroundColor: settings.emailEnabled ? '#6366f1' : '#e2e8f0' }}>
                <span style={{ ...styles.toggleKnob, transform: settings.emailEnabled ? 'translateX(20px)' : 'translateX(0)' }} />
              </span>
              <span style={styles.toggleLabel}>자동 동기화 활성화 (5분마다)</span>
            </label>
          </div>
          
          {settings.lastSyncAt && (
            <p style={styles.lastSync}>마지막 동기화: {settings.lastSyncAt.toLocaleString('ko-KR')}</p>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <div style={{ ...styles.sectionIconBox, backgroundColor: '#dbeafe' }}>
            <Link2 size={22} color="#3b82f6" />
          </div>
          <h2 style={styles.sectionTitle}>치과 이메일 매핑</h2>
        </div>
        
        <div style={styles.cardBody}>
          <p style={styles.description}>
            가져올 치과의 덴트웹/R2CAD 발신 이메일을 등록해주세요. 등록된 이메일에서 온 의뢰서만 가져옵니다.
          </p>
          
          {/* 입력 방식 선택 */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setIsManualInput(false)}
              style={{
                ...styles.modeButton,
                ...(isManualInput ? {} : styles.modeButtonActive)
              }}
            >
              📋 거래처에서 선택
            </button>
            <button
              onClick={() => {
                setIsManualInput(true);
                setNewMapping({ clinicId: '', clinicName: '', email: '' });
              }}
              style={{
                ...styles.modeButton,
                ...(isManualInput ? styles.modeButtonActive : {})
              }}
            >
              ✏️ 직접 입력
            </button>
          </div>
          
          {!isManualInput && connectedClinics.length === 0 ? (
            <div style={styles.warningBox}>
              <AlertCircle size={20} color="#d97706" />
              <div>
                <p style={styles.warningTitle}>연결된 치과가 없습니다</p>
                <p style={styles.warningText}>거래처 메뉴에서 치과와 연결하거나 "직접 입력"으로 등록해주세요.</p>
              </div>
            </div>
          ) : (
            <div style={styles.mappingForm}>
              {isManualInput ? (
                <div style={styles.inputBoxSmall}>
                  <Building2 size={20} color="#94a3b8" />
                  <input
                    type="text"
                    value={newMapping.clinicName}
                    onChange={(e) => setNewMapping(prev => ({ ...prev, clinicName: e.target.value }))}
                    placeholder="치과 이름 (예: 코코치과)"
                    style={styles.input}
                  />
                </div>
              ) : (
                <select
                  value={newMapping.clinicId}
                  onChange={(e) => setNewMapping(prev => ({ ...prev, clinicId: e.target.value }))}
                  style={styles.select}
                >
                  <option value="">치과 선택</option>
                  {connectedClinics.map(clinic => (
                    <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
                  ))}
                </select>
              )}
              <div style={styles.inputBoxSmall}>
                <Mail size={20} color="#94a3b8" />
                <input
                  type="email"
                  value={newMapping.email}
                  onChange={(e) => setNewMapping(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="덴트웹 발신 이메일 (예: 02-xxx-xxxx@dentweb.co.kr)"
                  style={styles.input}
                />
              </div>
              <button style={styles.addButton} onClick={addClinicMapping}>
                <Plus size={18} /> 추가
              </button>
            </div>
          )}
          
          <div style={styles.mappingList}>
            {clinicMappings.length === 0 ? (
              <p style={styles.emptyText}>등록된 매핑이 없습니다.</p>
            ) : (
              clinicMappings.map((mapping, idx) => (
                <div key={mapping.clinicId || mapping.clinicName || idx} style={styles.mappingItem}>
                  <div style={styles.mappingHeader}>
                    <Building2 size={16} color="#64748b" />
                    <span style={styles.mappingName}>{getClinicName(mapping)}</span>
                    {!mapping.clinicId && (
                      <span style={styles.manualBadge}>직접입력</span>
                    )}
                  </div>
                  <div style={styles.emailTags}>
                    {mapping.emails.map(email => (
                      <span key={email} style={styles.emailTag}>
                        {email}
                        <button style={styles.removeTagBtn} onClick={() => removeClinicMapping(mapping, email)}>
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 기간 설정 카드 */}
      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <div style={{ ...styles.sectionIconBox, backgroundColor: '#fef3c7' }}>
            <Calendar size={22} color="#d97706" />
          </div>
          <h2 style={styles.sectionTitle}>과거 의뢰서 가져오기</h2>
        </div>
        
        <div style={styles.cardBody}>
          <p style={styles.description}>
            기간을 설정하면 해당 기간의 의뢰서를 가져옵니다. 비워두면 새로운 의뢰서만 가져옵니다.
          </p>
          
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>치과 선택 (필수)</label>
            <select
              value={selectedClinicForSync}
              onChange={(e) => setSelectedClinicForSync(e.target.value)}
              style={styles.selectInput}
            >
              <option value="">-- 치과를 선택하세요 --</option>
              {clinicMappings.map((m, idx) => (
                <option key={m.clinicId || m.clinicName || idx} value={m.clinicId || m.clinicName}>
                  {getClinicName(m)}
                </option>
              ))}
            </select>
          </div>
          
          <div style={styles.dateRow}>
            <div style={styles.dateGroup}>
              <label style={styles.inputLabel}>시작일</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                style={styles.dateInput}
              />
            </div>
            <div style={styles.dateGroup}>
              <label style={styles.inputLabel}>종료일</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                style={styles.dateInput}
              />
            </div>
            <button 
              style={styles.clearDateBtn}
              onClick={() => setDateRange({ startDate: '', endDate: '' })}
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      <div style={styles.buttonRow}>
        <button style={styles.saveButton} onClick={handleSave} disabled={saving}>
          {saving ? <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={18} />}
          {saving ? '저장 중...' : '설정 저장'}
        </button>
        
        <button 
          style={{ ...styles.syncButton, opacity: (settings.emailEnabled && clinicMappings.length > 0) ? 1 : 0.5 }} 
          onClick={handleSync} 
          disabled={syncing || !settings.emailEnabled || clinicMappings.length === 0}
        >
          {syncing ? <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={18} />}
          {syncing ? '동기화 중...' : '지금 동기화'}
        </button>
      </div>
      
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9', padding: '24px', maxWidth: '800px', margin: '0 auto' },

  // Provider selector styles
  providerCard: { backgroundColor: '#fff', borderRadius: '16px', marginBottom: '20px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  providerHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 24px', borderBottom: '1px solid #f1f5f9' },
  providerTitle: { fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 },
  providerTabs: { display: 'flex', padding: '16px 24px', gap: '12px' },
  providerTab: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px 16px', border: '2px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#fff', fontSize: '15px', fontWeight: '600', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s' },
  providerTabActive: { backgroundColor: '#1ec800', color: '#fff', borderColor: '#1ec800' },
  providerTabActiveGmail: { backgroundColor: '#ea4335', color: '#fff', borderColor: '#ea4335' },
  providerTabActiveDaum: { backgroundColor: '#ffcd00', color: '#000', borderColor: '#ffcd00' },
  providerIcon: { width: '24px', height: '24px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', backgroundColor: '#1ec800', color: '#fff' },

  guideCard: { backgroundColor: '#f0f9ff', border: '2px solid #0ea5e9', borderRadius: '16px', marginBottom: '20px', overflow: 'hidden' },
  guideHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '18px 24px', backgroundColor: '#e0f2fe', borderBottom: '1px solid #bae6fd' },
  guideTitle: { fontSize: '17px', fontWeight: '700', color: '#0369a1', margin: 0 },
  guideBody: { padding: '20px 24px' },
  guideItem: { display: 'flex', gap: '14px', marginBottom: '18px' },
  guideStep: { width: '28px', height: '28px', backgroundColor: '#0ea5e9', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', flexShrink: 0 },
  guideContent: { flex: 1 },
  guideLabel: { fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '0 0 6px 0' },
  guideText: { fontSize: '14px', color: '#475569', margin: 0, lineHeight: 1.6 },
  guideWarning: { fontSize: '13px', color: '#dc2626', margin: '6px 0 0 0', fontWeight: '500' },
  guideSubtext: { fontSize: '13px', color: '#64748b', margin: '6px 0 0 0' },
  
  card: { backgroundColor: '#fff', borderRadius: '16px', marginBottom: '20px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f1f5f9', gap: '14px' },
  sectionIconBox: { width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sectionTitle: { fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 },
  cardBody: { padding: '24px' },
  description: { fontSize: '14px', color: '#64748b', marginBottom: '24px', lineHeight: 1.5 },
  inputGroup: { marginBottom: '20px' },
  inputLabel: { display: 'block', fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '10px' },
  inputBox: { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '0 16px', height: '56px', gap: '12px' },
  inputBoxSmall: { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '0 16px', height: '48px', flex: 1, gap: '12px' },
  input: { flex: 1, fontSize: '16px', color: '#0f172a', border: 'none', background: 'transparent', outline: 'none' },
  eyeBtn: { backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' },
  toggleRow: { marginTop: '20px' },
  toggleContainer: { display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' },
  toggleInput: { display: 'none' },
  toggleSwitch: { width: '48px', height: '28px', borderRadius: '14px', position: 'relative', transition: 'background-color 0.2s' },
  toggleKnob: { width: '24px', height: '24px', backgroundColor: '#fff', borderRadius: '50%', position: 'absolute', top: '2px', left: '2px', transition: 'transform 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  toggleLabel: { fontSize: '15px', color: '#374151', fontWeight: '500' },
  lastSync: { fontSize: '13px', color: '#94a3b8', marginTop: '16px' },
  mappingForm: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  select: { flex: 1, minWidth: '150px', height: '48px', padding: '0 16px', fontSize: '15px', border: '1px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#f8fafc', color: '#0f172a', outline: 'none', cursor: 'pointer' },
  addButton: { display: 'flex', alignItems: 'center', gap: '6px', padding: '0 20px', height: '48px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  mappingList: { marginTop: '16px' },
  emptyText: { textAlign: 'center', color: '#94a3b8', padding: '32px', fontSize: '15px' },
  mappingItem: { backgroundColor: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '12px' },
  mappingHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' },
  mappingName: { fontSize: '15px', fontWeight: '600', color: '#374151' },
  manualBadge: { padding: '2px 8px', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '6px', fontSize: '12px', fontWeight: '600', marginLeft: '8px' },
  modeButton: { flex: 1, padding: '12px 20px', fontSize: '15px', fontWeight: '600', border: '2px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#fff', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s' },
  modeButtonActive: { backgroundColor: '#6366f1', color: '#fff', borderColor: '#6366f1' },
  warningBox: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', backgroundColor: '#fef3c7', borderRadius: '12px', marginBottom: '20px' },
  warningTitle: { fontSize: '15px', fontWeight: '600', color: '#92400e', margin: '0 0 4px 0' },
  warningText: { fontSize: '14px', color: '#a16207', margin: 0 },
  emailTags: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  emailTag: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#374151' },
  removeTagBtn: { backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', color: '#ef4444' },
  dateRow: { display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' },
  dateGroup: { flex: 1, minWidth: '150px' },
  dateInput: { width: '100%', height: '48px', padding: '0 16px', fontSize: '15px', border: '1px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#f8fafc', color: '#0f172a', outline: 'none' },
  selectInput: { width: '100%', height: '52px', padding: '0 16px', fontSize: '15px', border: '1px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#f8fafc', color: '#0f172a', outline: 'none', cursor: 'pointer' },
  clearDateBtn: { height: '48px', padding: '0 16px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', color: '#64748b', cursor: 'pointer' },
  buttonRow: { display: 'flex', gap: '12px', marginTop: '8px' },
  saveButton: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '18px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  syncButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '18px 24px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' },
  spinner: { width: '50px', height: '50px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { marginTop: '16px', fontSize: '16px', color: '#64748b' },
  successMsg: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px', backgroundColor: '#dcfce7', borderRadius: '12px', marginBottom: '20px', fontSize: '15px', fontWeight: '500', color: '#166534' },
  errorMsg: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px', backgroundColor: '#fee2e2', borderRadius: '12px', marginBottom: '20px', fontSize: '15px', fontWeight: '500', color: '#991b1b' }
};

export default LabEmailSettings;